import { useEffect, useState } from 'react';
import { useChatStore } from '../store/chat-store';

/**
 * Full-widget banner shown when the server tells us this user is banned.
 * Permanent / temp bans replace the whole chat surface; mute bans stay open
 * but the composer shows a 'you are muted' state instead.
 */
export function BannedScreen() {
  const ban = useChatStore(s => s.ban);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!ban?.expiresAt) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [ban?.expiresAt]);

  if (!ban || ban.type === 'mute') return null;

  const expiresAt = ban.expiresAt ? new Date(ban.expiresAt).getTime() : null;
  const remainingMs = expiresAt ? Math.max(0, expiresAt - now) : null;
  const remainingLabel = remainingMs !== null ? formatRemaining(remainingMs) : null;
  const title = ban.type === 'permanent' ? 'You are permanently banned' : 'You are temporarily banned';

  return (
    <div className="flex flex-1 flex-col items-center justify-center text-center px-8 py-12 bg-gradient-to-b from-red-50 via-white to-white">
      <div className="text-5xl mb-3" aria-hidden="true">🚫</div>
      <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
      <p className="text-sm text-gray-700 mt-2 max-w-md">{ban.reason}</p>
      {remainingLabel && (
        <p className="text-xs text-gray-500 mt-3">
          Access restored in <span className="font-semibold text-gray-700">{remainingLabel}</span>
        </p>
      )}
      <p className="text-[11px] text-gray-400 mt-6">
        If you believe this is a mistake, contact support.
      </p>
    </div>
  );
}

function formatRemaining(ms: number): string {
  const totalSec = Math.ceil(ms / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}
